import React, { useEffect, useMemo, useState } from 'react';
import { CURRENCY_CONFIG, resolveCountryCodeSync, resolveCountryCodeAsync, saveManualCountry, formatPriceForCountry, CountryCode } from '../utils/priceConfig';
/**
 * useCurrency — Synchronous-first currency hook
 *
 * Resolves country from local sources FIRST (no network, no flash).
 * Only falls back to GeoIP if all local sources fail.
 *
 * Returns:
 *   countryCode — resolved country
 *   isReady — true once country is resolved (instant for cached/locale users)
 *   source — how the country was detected
 *   formatPrice(usdAmount, priceType?) — complete formatted string
 */
export function useCurrency() {
  // Try synchronous resolution IMMEDIATELY (before first render completes)
  const syncResult = useMemo(() => resolveCountryCodeSync(), []);
  const [countryCode, setCountryCode] = useState<CountryCode>(syncResult ? syncResult.code : 'US');
  const [isReady, setIsReady] = useState(syncResult !== null);
  const [source, setSource] = useState(syncResult?.source || 'pending');
  useEffect(() => {
    // If sync already resolved, we're done — no async needed
    if (syncResult) {
      setCountryCode(syncResult.code);
      setSource(syncResult.source);
      setIsReady(true);
      return;
    }
    // Sync failed → need async GeoIP (non-blocking)
    let cancelled = false;
    resolveCountryCodeAsync().then(result => {
      if (!cancelled) {
        setCountryCode(result.code);
        setSource(result.source);
        setIsReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [syncResult]);
  const formatPrice = (usdAmount: number, priceType: 'base' | 'compareAt' = 'base'): string => {
    return formatPriceForCountry(usdAmount, countryCode, priceType);
  };
  const changeCountry = (code: CountryCode) => {
    setCountryCode(code);
    saveManualCountry(code);
    setSource('manual');
    setIsReady(true);
  };
  const currency = {
    ...CURRENCY_CONFIG[countryCode],
    countryCode
  };
  return {
    currency,
    countryCode,
    isLoading: !isReady,
    isReady,
    source,
    formatPrice,
    changeCountry,
    availableCountries: Object.keys(CURRENCY_CONFIG) as CountryCode[]
  };
}